import React, { useMemo, useState } from 'react';
import { useFilterSortContext } from '../utils/filterSortContext';
import { CatalogOption, FilterableField, FilterIDS, FilterTypes } from '../utils/types';
import FilterItem from './FilterItem';
import ToggleFiltersButton from './ToggleFiltersButton';

const INITIAL_FILTERS_LIMIT = 4;

interface FilterListProps {
    values: Array<FilterableField>;
    currentSelection: {
        [filterName: string]: FilterableField;
    };
    onChange: (filterValue: FilterableField) => void;
    optionsMap: Map<string, CatalogOption>;
}

function FiltersList({ values, currentSelection, onChange, optionsMap }: FilterListProps) {
    const { inDesignMode, hasProducts, currentPageType } =
        useFilterSortContext();
    const [isExpanded, setIsExpanded] = useState(false);

    const visibleFilters = useMemo(() => {
        return values.filter((filterField) => {
            if (filterField.disabled && !inDesignMode) {
                return false;
            }
            return !shouldHideFilterItem({
                filterField,
                hasProducts,
                currentPageType,
            });
        });
    }, [values, inDesignMode, hasProducts, currentPageType]);

    const displayedFilters = useMemo(() => {
        return isExpanded
            ? visibleFilters
            : visibleFilters.slice(0, INITIAL_FILTERS_LIMIT);
    }, [isExpanded, visibleFilters]);

    const shouldShowToggle = visibleFilters.length > INITIAL_FILTERS_LIMIT;
    const hiddenCount = visibleFilters.length - INITIAL_FILTERS_LIMIT;

    return (
        <>
            {displayedFilters.map((filterField) => {
                const filterId = filterField.fieldId;
                const filterType = filterField.filterType || FilterTypes.ONE_OF;
                const filterSelectedValues =
                    currentSelection[filterId]?.selectedValues || undefined;
                const optionValue = optionsMap.get(filterField.fieldDisplayName.toLowerCase());
                return (
                    <FilterItem
                        key={filterId}
                        item={filterField}
                        type={filterType}
                        currentValues={filterSelectedValues}
                        onChange={(selectedValues) => {
                            onChange({
                                ...filterField,
                                filterType,
                                selectedValues,
                            });
                        }}
                        optionValue={optionValue}
                    />
                );
            })}
            {shouldShowToggle && (
                <ToggleFiltersButton
                    isExpanded={isExpanded}
                    count={hiddenCount}
                    onClick={() => setIsExpanded(!isExpanded)}
                />
            )}
        </>
    );
}

function shouldHideFilterItem({
    filterField,
    hasProducts,
    currentPageType,
}: {
    filterField: FilterableField;
    hasProducts?: boolean;
    currentPageType?: string;
}) {
    const filterId = filterField.fieldId;
    if (filterId === FilterIDS.CATEGORY_ID) {
        const isCategoryPage = currentPageType === 'STORE_CATEGORY_PAGE';
        return (
            isCategoryPage || Object.keys(filterField.filtersData).length === 0
        );
    }
    if (filterId === FilterIDS.PRICE) {
        return !hasProducts;
    }
}

export default FiltersList;
